import _ from "lodash";

export const filterEstimations = (
  estimationsList,
  productList,
  filters,
  groupBy
) => {
  return _.pickBy(estimationsList, (estimation) => {
    if (productList !== "" && !productList.includes(estimation.product_id)) {
      return false;
    }

    const someRetailerGroup = Array.from(filters.retailerGroup || []).some(
      (retailerGroup) =>
        retailerGroup.id === estimation.retailer.retailer_group_id
    );

    const someRetailer = Array.from(filters?.retailer || []).some(
      (retailer) => retailer.id === estimation.retailer_id
    );

    if (filters.retailer && !someRetailer) {
      return false;
    }

    if (filters.retailerGroup) {
      if (!filters.retailer && !someRetailerGroup) return false;
    }

    if (
      filters.area &&
      !Array?.from(filters?.area).find((area) => area.id === estimation.area_id)
    ) {
      return false;
    }

    if (filters.searchTerm) {
      const lowerCaseSearchTerm = filters.searchTerm.toLowerCase();

      const isAreaInSearchTerm = estimation.area.label
        .toLowerCase()
        .includes(lowerCaseSearchTerm);

      const isRetailerInSearchTerm = estimation.retailer.label
        .toLowerCase()
        .includes(lowerCaseSearchTerm);

      const isProductInSearchTerm = estimation.product.label
        .toLowerCase()
        .includes(lowerCaseSearchTerm);

      if (!["retailer", "product"].includes(groupBy) && isAreaInSearchTerm)
        return true;

      if (!["area", "product"].includes(groupBy) && isRetailerInSearchTerm)
        return true;

      if (isProductInSearchTerm) return true;

      return false;
    }

    return true;
  });
};

export const reduceEstimations = (
  estimationsList,
  groupBy,
  metadata,
  referenceList,
  NewInvestment = false
) => {
  const groupedAndReducedEstimations = _.map(
    estimationsList,
    (estimationsGroup) => {
      let referenceEstimation;
      return {
        product: estimationsGroup[0].product,
        product_id: estimationsGroup[0].product_id,
        retailer_group:
          groupBy === "retailerGroup"
            ? estimationsGroup[0].retailer.retailer_groups
            : null,
        retailer_group_id:
          groupBy === "retailerGroup"
            ? estimationsGroup[0].retailer.retailer_groups.id
            : undefined,
        retailer: groupBy === "retailer" ? estimationsGroup[0].retailer : null,
        retailer_id:
          groupBy === "retailer" ? estimationsGroup[0].retailer_id : undefined,
        area: groupBy === "area" ? estimationsGroup[0].area : null,
        area_id: groupBy === "area" ? estimationsGroup[0].area_id : undefined,
        price:
          metadata.price[0].visibility_type !== 0
            ? _.round(
                _.sumBy(estimationsGroup, (estimation) => {
                  return estimation.price * estimation.units;
                }) / _.sumBy(estimationsGroup, `units`),
                metadata.price[0].decimals
              )
            : null,
        price_out_wo_taxes:
          metadata.price[0].visibility_type !== 0
            ? _.round(
                _.sumBy(estimationsGroup, (estimation) => {
                  return estimation.price_out_wo_taxes * estimation.units;
                }) / _.sumBy(estimationsGroup, `units`),
                metadata.price[0].decimals
              )
            : null,
        price_in:
          metadata.price_in[0].visibility_type !== 0
            ? _.round(
                _.sumBy(estimationsGroup, (estimation) => {
                  return estimation.price_in * estimation.units_in;
                }) / _.sumBy(estimationsGroup, `units_in`),
                metadata.price_in[0].decimals
              )
            : null,
        price_in_wo_taxes:
          metadata.price_in[0].visibility_type !== 0
            ? _.round(
                _.sumBy(estimationsGroup, (estimation) => {
                  return estimation.price_in_wo_taxes * estimation.units_in;
                }) / _.sumBy(estimationsGroup, `units_in`),
                metadata.price_in[0].decimals
              )
            : null,
        units:
          metadata.units[0].visibility_type !== 0
            ? _.sumBy(estimationsGroup, "units")
            : null,
        units_in:
          metadata.units_in[0].visibility_type !== 0
            ? _.sumBy(estimationsGroup, "units_in")
            : null,
        margin:
          metadata.margin[0].visibility_type !== 0
            ? _.sumBy(estimationsGroup, "margin")
            : null,
        sales:
          metadata.sales[0].visibility_type !== 0
            ? _.sumBy(estimationsGroup, "sales")
            : null,
        sales_in:
          metadata.sales_in[0].visibility_type !== 0
            ? _.sumBy(estimationsGroup, "sales_in")
            : null,
        investment_in:
          metadata.investment_in[0].visibility_type !== 0 && referenceList
            ? _.reduce(
                estimationsGroup,
                (total, estimation) => {
                  if (referenceList)
                    referenceEstimation =
                      referenceList[
                        `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
                      ];
                  // Usar price_in_wo_taxes si NewInvestment es true, si no usar price_in
                  const refPriceIn = NewInvestment
                    ? referenceEstimation?.price_in_wo_taxes
                    : referenceEstimation?.price_in;
                  const estPriceIn = NewInvestment
                    ? estimation.price_in_wo_taxes
                    : estimation.price_in;
                  return (
                    total +
                    (refPriceIn < estPriceIn
                      ? 0
                      : (refPriceIn - estPriceIn) * estimation.units_in)
                  );
                },
                0
              )
            : null,
        investment:
          metadata.investment[0].visibility_type !== 0 && referenceList
            ? _.reduce(
                estimationsGroup,
                (total, estimation) => {
                  if (referenceList)
                    referenceEstimation =
                      referenceList[
                        `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
                      ];
                  // Usar price_out_wo_taxes si NewInvestment es true, si no usar price
                  const refPrice = NewInvestment
                    ? referenceEstimation?.price_out_wo_taxes
                    : referenceEstimation?.price;
                  const estPrice = NewInvestment
                    ? estimation.price_out_wo_taxes
                    : estimation.price;
                  return (
                    total +
                    (refPrice < estPrice
                      ? 0
                      : (refPrice - estPrice) * estimation.units)
                  );
                },
                0
              )
            : null,
      };
    }
  );

  return _.keyBy(
    groupedAndReducedEstimations,
    (estimation) =>
      `${estimation.product.id}-${estimation.area_id}-${estimation.retailer_id}`
  );
};

export const groupEstimationsByKeyAndProduct = (estimationsList, groupBy) => {
  const estimationsGroupedByKey = _.groupBy(estimationsList, (estimation) =>
    groupBy !== "retailerGroup"
      ? estimation[groupBy].id
      : estimation.retailer.retailer_groups.id
  );

  if (groupBy === "product") {
    return estimationsGroupedByKey;
  }

  // We always group by product too after grouping by other key
  _.each(estimationsGroupedByKey, (estimationsGroup, key) => {
    estimationsGroupedByKey[key] = _.groupBy(
      estimationsGroup,
      (estimation) => estimation.product.id
    );
  });
  return estimationsGroupedByKey;
};

export const groupByPriceAndReduceEstimations = (
  estimationsList,
  metadata,
  preffix
) => {
  const estimationsGroupedByPrice = _.groupBy(
    estimationsList,
    (estimation) => estimation.price
  );

  return _.map(estimationsGroupedByPrice, (estimationsGroup) => {
    return {
      price:
        metadata.price[0].visibility_type !== 0
          ? _.round(
              _.sumBy(estimationsGroup, (estimation) => {
                return estimation.price * estimation[`${preffix}units`];
              }) / _.sumBy(estimationsGroup, `${preffix}units`),
              metadata.price[0].decimals
            )
          : null,
      price_out_wo_taxes:
        metadata.price[0].visibility_type !== 0
          ? _.round(
              _.sumBy(estimationsGroup, (estimation) => {
                return (
                  estimation.price_out_wo_taxes * estimation[`${preffix}units`]
                );
              }) / _.sumBy(estimationsGroup, `${preffix}units`),
              metadata.price[0].decimals
            )
          : null,
      price_in:
        metadata.price_in[0].visibility_type !== 0
          ? _.round(
              _.sumBy(estimationsGroup, (estimation) => {
                return estimation.price_in * estimation[`${preffix}units_in`];
              }) / _.sumBy(estimationsGroup, `${preffix}units_in`),
              metadata.price_in[0].decimals
            )
          : null,
      price_in_wo_taxes:
        metadata.price_in[0].visibility_type !== 0
          ? _.round(
              _.sumBy(estimationsGroup, (estimation) => {
                return (
                  estimation.price_in_wo_taxes *
                  estimation[`${preffix}units_in`]
                );
              }) / _.sumBy(estimationsGroup, `${preffix}units_in`),
              metadata.price_in[0].decimals
            )
          : null,
      units:
        metadata.units[0].visibility_type !== 0
          ? _.sumBy(estimationsGroup, `${preffix}units`)
          : null,
      units_in:
        metadata.units_in[0].visibility_type !== 0
          ? _.sumBy(estimationsGroup, `${preffix}units_in`)
          : null,
      margin:
        metadata.margin[0].visibility_type !== 0
          ? _.sumBy(estimationsGroup, `${preffix}margin`)
          : null,
      sales:
        metadata.sales[0].visibility_type !== 0
          ? _.sumBy(estimationsGroup, `${preffix}sales`)
          : null,
      delta_units:
        metadata.units[0].visibility_type !== 0
          ? _.sumBy(estimationsGroup, `${preffix}delta_units`)
          : null,
      delta_units_in:
        metadata.units_in[0].visibility_type !== 0
          ? _.sumBy(estimationsGroup, `${preffix}delta_units_in`)
          : null,
      delta_margin:
        metadata.margin[0].visibility_type !== 0
          ? _.sumBy(estimationsGroup, `${preffix}delta_margin`)
          : null,
      delta_sales:
        metadata.sales[0].visibility_type !== 0
          ? _.sumBy(estimationsGroup, `${preffix}delta_sales`)
          : null,
      sales_in:
        metadata.sales_in[0].visibility_type !== 0
          ? _.sumBy(estimationsGroup, `${preffix}sales_in`)
          : null,
      event_price:
        metadata.price[0].visibility_type !== 0
          ? _.round(
              _.sumBy(estimationsGroup, (estimation) => {
                return (
                  estimation.avg_weight_by_delta_units_event_price *
                  estimation[`${preffix}delta_units`]
                );
              }) / _.sumBy(estimationsGroup, `${preffix}delta_units`),
              metadata.price[0].decimals
            )
          : null,
      delta_units_intercept:
        metadata.price[0].visibility_type !== 0
          ? _.sumBy(
              estimationsGroup,
              `avg_weight_by_units_plus_delta_units_intercept`
            )
          : null,
      delta_units_slope:
        metadata.price[0].visibility_type !== 0
          ? _.sumBy(
              estimationsGroup,
              `avg_weight_by_units_plus_delta_units_slope`
            )
          : null,
    };
  });
};
